import * as React from "react";
import type { AnalysisParagraphResult } from "../../../agreement";
import type { ConfirmAction } from "../types";

export type UseBulkActionsOptions = {
  pendingNonCompliantKeys: Array<{ row: AnalysisParagraphResult; key: string }>;
  editedTextByKey: Record<string, string>;
  aiLoading: boolean;
  aiApplyingKey: string | null;
  clearEditState: (key: string) => void;
  onAcceptSuggestion: (
    row: AnalysisParagraphResult,
    key: string,
    options?: { revealSelection?: boolean; overrideText?: string; skipRefresh?: boolean }
  ) => Promise<void> | void;
  onRejectSuggestion: (
    row: AnalysisParagraphResult,
    key: string,
    options?: { skipRefresh?: boolean }
  ) => Promise<void> | void;
  onRefreshAiAnalysis?: () => Promise<void> | void;
};

export function useBulkActions({
  pendingNonCompliantKeys,
  editedTextByKey,
  aiLoading,
  aiApplyingKey,
  clearEditState,
  onAcceptSuggestion,
  onRejectSuggestion,
  onRefreshAiAnalysis,
}: UseBulkActionsOptions) {
  const [acceptAllRunning, setAcceptAllRunning] = React.useState(false);
  const [rejectAllRunning, setRejectAllRunning] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState<ConfirmAction>(null);

  const confirmTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    };
  }, []);

  const requestConfirm = React.useCallback((action: ConfirmAction) => {
    setConfirmAction(action);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(() => setConfirmAction(null), 4000);
  }, []);

  const cancelConfirm = React.useCallback(() => {
    setConfirmAction(null);
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, []);

  const handleAcceptAll = React.useCallback(async () => {
    if (aiLoading || acceptAllRunning || pendingNonCompliantKeys.length === 0) return;
    setAcceptAllRunning(true);
    try {
      for (const item of pendingNonCompliantKeys) {
        const overrideText = editedTextByKey[item.key];
        await Promise.resolve(
          onAcceptSuggestion(item.row, item.key, {
            revealSelection: true,
            skipRefresh: true,
            ...(overrideText !== undefined ? { overrideText } : {}),
          })
        );
        clearEditState(item.key);
      }
      try {
        await Promise.resolve(onRefreshAiAnalysis?.());
      } catch {
        /* best-effort */
      }
    } finally {
      setAcceptAllRunning(false);
      setConfirmAction(null);
    }
  }, [
    aiLoading,
    acceptAllRunning,
    pendingNonCompliantKeys,
    editedTextByKey,
    onAcceptSuggestion,
    clearEditState,
    onRefreshAiAnalysis,
  ]);

  const handleRejectAll = React.useCallback(async () => {
    if (aiLoading || rejectAllRunning || pendingNonCompliantKeys.length === 0) return;
    setRejectAllRunning(true);
    try {
      for (const item of pendingNonCompliantKeys) {
        clearEditState(item.key);
        await Promise.resolve(onRejectSuggestion(item.row, item.key, { skipRefresh: true }));
      }
      try {
        await Promise.resolve(onRefreshAiAnalysis?.());
      } catch {
        /* best-effort */
      }
    } finally {
      setRejectAllRunning(false);
      setConfirmAction(null);
    }
  }, [
    aiLoading,
    rejectAllRunning,
    pendingNonCompliantKeys,
    clearEditState,
    onRejectSuggestion,
    onRefreshAiAnalysis,
  ]);

  const onAcceptAllClick = React.useCallback(() => {
    if (confirmAction === "accept") {
      void handleAcceptAll();
    } else {
      requestConfirm("accept");
    }
  }, [confirmAction, handleAcceptAll, requestConfirm]);

  const onRejectAllClick = React.useCallback(() => {
    if (confirmAction === "reject") {
      void handleRejectAll();
    } else {
      requestConfirm("reject");
    }
  }, [confirmAction, handleRejectAll, requestConfirm]);

  const bulkBusy = acceptAllRunning || rejectAllRunning || aiApplyingKey !== null;

  return {
    acceptAllRunning,
    rejectAllRunning,
    confirmAction,
    bulkBusy,
    onAcceptAllClick,
    onRejectAllClick,
    cancelConfirm,
    handleAcceptAll,
    handleRejectAll,
  };
}
